/** Proposal 넛지 카드 — 이 카드로 이미 Submit 했는지 (같은 카드 재발송 차단). */

// node 단위 테스트에서도 로드되도록 확장자를 쓴다(tsconfig allowImportingTsExtensions).
import { isAdminPreview } from "../lib/adminPreview.ts";

// Vite 밖(node 단위 테스트)에서는 import.meta.env 가 없다.
const API_BASE = import.meta.env?.VITE_API_BASE || "";

/**
 * Submit 은 BrFrame 모달이 하므로 위젯은 결과를 모른다 — 서버에 기록된 상태를 묻는다.
 * 실패·미리보기는 ``false`` (CTA 를 막지 않는다).
 */
export async function fetchSummaryNudgeSubmitted(
  sessionToken: string,
  resultId: string,
): Promise<boolean> {
  const sid = String(sessionToken || "").trim();
  const rid = String(resultId || "").trim();
  if (!sid || !rid || isAdminPreview()) return false;
  const url =
    `${API_BASE}/api/commerce/session/${encodeURIComponent(sid)}/summary-nudge-status` +
    `?resultId=${encodeURIComponent(rid)}`;
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return false;
    const body = (await res.json()) as { submitted?: unknown };
    return body.submitted === true;
  } catch {
    return false;
  }
}

/** CTA 클릭 후 상태 확인 간격(ms) — 처음엔 촘촘히, 모달을 오래 두면 느슨하게. */
export const NUDGE_STATUS_POLL_DELAYS_MS = [1000, 1000, 1500, 1500, 2000, 3000, 5000];
/** 이 시간이 지나면 확인을 멈춘다 — 모달을 닫고 떠난 경우. 다음 클릭 때 다시 확인. */
export const NUDGE_STATUS_POLL_MAX_MS = 10 * 60 * 1000;

export function nudgeStatusPollDelay(attempt: number): number {
  const delays = NUDGE_STATUS_POLL_DELAYS_MS;
  return delays[Math.min(attempt, delays.length - 1)];
}

/**
 * 모달을 열기 전 클릭 기록(resultId) 저장을 기다리는 상한(ms).
 * 넘기면 모달은 그대로 연다 — 발송을 막지 않고, 이때만 같은 카드 차단이 빠질 수 있다.
 */
export const CLICK_RECORD_WAIT_MS = 3000;

export interface SubmittedPoller {
  /** 진행 중인 확인을 버리고 처음부터 다시 확인한다. */
  start(): void;
  /** 예약된 확인을 지우고, 응답 대기 중인 확인의 다음 예약도 막는다. */
  stop(): void;
}

interface PollerClock {
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

/**
 * CTA 클릭 후 Submit 도달 확인. 세대 번호로 stop·재시작 이후 늦게 끝난 응답이
 * 다음 확인을 예약하거나 ``onSubmitted`` 를 부르지 못하게 한다.
 */
export function createSubmittedPoller(
  check: () => Promise<boolean>,
  onSubmitted: () => void,
  clock: PollerClock = {},
): SubmittedPoller {
  const now = clock.now ?? Date.now;
  const setTimer = clock.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer =
    clock.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  let generation = 0;
  let timer: unknown = null;

  const stop = () => {
    generation += 1;
    if (timer !== null) {
      clearTimer(timer);
      timer = null;
    }
  };

  const start = () => {
    stop();
    const mine = generation;
    const startedAt = now();
    let attempt = 0;
    const tick = async () => {
      timer = null;
      const done = await check();
      if (mine !== generation) return;
      if (done) {
        onSubmitted();
        return;
      }
      if (now() - startedAt >= NUDGE_STATUS_POLL_MAX_MS) return;
      timer = setTimer(tick, nudgeStatusPollDelay(++attempt));
    };
    timer = setTimer(tick, nudgeStatusPollDelay(attempt));
  };

  return { start, stop };
}

/** ``task`` 가 끝나거나 ``ms`` 가 지나면 resolve. reject 하지 않는다. */
export function settleWithin(task: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    task.then(done, done);
  });
}
